import { createFileRoute } from "@tanstack/react-router";
import { DEFAULT_OPTION_CHOICES, DEFAULT_STUDIO_DOC_NAME } from "building-types/doc/docDefaultConstants";
import { BuildingViewerViewMode } from "building-types/geometry/buildingGeometryTypes";
import { TextFonts } from "common/constants/textFont";
import { assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import { uploadFile } from "common/utils/uploadFile";
import { EnvPreset } from "gfx/EnvPreset";
import { SSFXRendererOutput } from "gfx/threejs/higharc/SSFXRendererOutput";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import styles from "lib/components/debug/buildingDebugger.module.scss";
import BuildingDebuggerControls from "lib/components/debug/BuildingDebuggerControls";
import DebugBuildingViewer from "lib/components/design/DebugBuildingViewer";
import SceneStateHUD from "lib/components/design/SceneStateHUD";
import usePlanSetFonts from "lib/components/studio/editorViewer/usePlanSetFonts";
import { studioDefaultView3DFilter } from "lib/components/studio/studioViewFilterDefaults";
import View3DFilterControls from "lib/components/studio/View3DFilterControls";
import { SiteType, defaultSceneState } from "lib/house/render/buildingViewerSceneState";
import { initScreenshotCamera } from "lib/house/render/screenshotCamera";
import { screenshotOfBuilding } from "lib/house/render/screenshotOfBuilding";
import { defaultView3DFilter } from "lib/house/render/viewFilterDefaults";
import createCustomizerStore from "lib/state/createCustomizerStore";
import { setLoading, updateBuildingDoc } from "lib/state/customizerActions";
import type { Customizer } from "lib/state/customizerContext";
import { LoadState, useCustomizerDispatch, useCustomizerSelector } from "lib/state/customizerContext";
import { setViewFilter, setViewMode } from "lib/state/sceneStateActions";
import getPageTitle from "lib/utils/getPageTitle";
import { useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { Provider } from "react-redux";
import Loading from "ui/components/Loading";
import withAuthentication from "../../components/withAuthentication";

const initialState: Customizer = {
  building: null,
  fatalError: null,
  sceneState: {
    ...defaultSceneState,
    siteType: SiteType.FALLBACK_TO_GRASS,
    envPreset: EnvPreset.MORNING_HDR,
    exteriorLightProbesEnabled: true,
    viewFilter: defaultView3DFilter(),
  },
  loading: LoadState.FULLSCREEN,
  name: DEFAULT_STUDIO_DOC_NAME,
  optionChoices: DEFAULT_OPTION_CHOICES,
};

const store = createCustomizerStore(initialState);

const DebugBuildingDebuggerInternal = () => {
  const { renderStageFilter, loading, building } = useCustomizerSelector((cus) => {
    return {
      building: cus.building,
      loading: cus.loading,
      renderStageFilter: cus.sceneState.viewFilter,
    };
  });
  const dispatch = useCustomizerDispatch();

  const fontsLoaded = usePlanSetFonts(Object.values(TextFonts));

  const openBuilding = async () => {
    const { FILE_EXT, docParse } = await import("doc/docMarshal");
    const file = await uploadFile("application/json," + FILE_EXT);

    const doc = docParse(file.body);

    setScreenshotURL("");
    dispatch(setLoading(LoadState.FULLSCREEN));
    dispatch(updateBuildingDoc(DEFAULT_OPTION_CHOICES, doc));
    dispatch(setViewMode(BuildingViewerViewMode.EXTERIOR));
  };

  const containerRef = useRef(null);

  const [screenshotURL, setScreenshotURL] = useState("");
  const updateScreenshot = async () => {
    const pixelWidth = 800;
    const pixelHeight = 600;
    assertIsNotNullOrUndefined(building);
    const camera = await initScreenshotCamera({ building, aspect: pixelWidth / pixelHeight });
    const url = await screenshotOfBuilding({
      building,
      width: pixelWidth,
      height: pixelHeight,
      camera,
      output: SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS,
    });
    setScreenshotURL(ensureDefined(url));
  };

  const viewerSystem = useRef<THREEViewerSystem | null>(null);

  return (
    <>
      <Head>
        <title>{getPageTitle("Building Debugger")}</title>
      </Head>

      <div className={styles.paramsContainer}>
        <button onClick={openBuilding}>Open building</button>

        <BuildingDebuggerControls viewerSystem={viewerSystem} />
      </div>

      {building && fontsLoaded && (
        <div className={styles.viewerContainer} ref={containerRef}>
          {loading && <Loading />}
          <DebugBuildingViewer debugFeaturesEnabled viewerSystem={viewerSystem} />
          <SceneStateHUD />
          <View3DFilterControls
            filter={renderStageFilter}
            onChange={(f) => dispatch(setViewFilter(f))}
            defaultFilter={studioDefaultView3DFilter}
          />
          <div style={{ position: "absolute", top: 100, left: 10, width: 200, height: 150, background: "grey" }}>
            {screenshotURL && <img style={{ width: "100%" }} src={screenshotURL} />}
            <button style={{ position: "absolute", bottom: 0, left: 0 }} onClick={updateScreenshot}>
              Update
            </button>
          </div>
        </div>
      )}
    </>
  );
};

function DebugBuildingDebuggerPage() {
  return (
    <Provider store={store}>
      <DebugBuildingDebuggerInternal />
    </Provider>
  );
}

export const Route = createFileRoute("/debug/building-debugger")({
  component: withAuthentication(DebugBuildingDebuggerPage, { superAdmin: true }),
});
